import type { QuoteDraftLinePreview, VatAlignmentDto } from './types'

// SOLY-VAT-001: returns the subset of draft lines that the bulk-align endpoint will accept.
// The BE owns the eligibility rule (`QuoteLine.IsEligibleForVatAlignment` in Soly.Sales.Domain);
// the FE filters via the `mismatchingLineIds` set the BE already computed and only proposes the
// ones the user can act on. Lines with `vatPercent == null` are excluded — un-priced lines have
// no totals to recompute and would yield a banner the user cannot meaningfully resolve.
export function selectAlignableLines(
  lines: QuoteDraftLinePreview[],
  alignment: VatAlignmentDto
): QuoteDraftLinePreview[] {
  const mismatchingIds = new Set(alignment.mismatchingLineIds)
  return lines.filter(
    (l) => mismatchingIds.has(l.id) && l.vatPercent !== null && l.vatPercent !== undefined
  )
}

// Default selection state — all proposed lines pre-checked. The user can untick exceptions
// (e.g. a service line that should keep its own VAT) before submitting.
export function defaultSelectedIds(lines: QuoteDraftLinePreview[]): Record<string, boolean> {
  return lines.reduce<Record<string, boolean>>((acc, l) => {
    acc[l.id] = true
    return acc
  }, {})
}

export function countSelected(selected: Record<string, boolean>): number {
  return Object.values(selected).filter(Boolean).length
}

export function selectedIds(selected: Record<string, boolean>): string[] {
  return Object.entries(selected)
    .filter(([, v]) => v)
    .map(([id]) => id)
}

// SessionStorage key for the dismiss state — scoped per quote version so the banner can
// re-appear on a different version. Closing the browser drops the dismiss (intentional —
// the user might genuinely want a reminder on the next session if they haven't acted yet).
export function dismissStorageKey(versionId: string): string {
  return `soly-vat-mismatch-dismissed:${versionId}`
}

export function isDismissed(versionId: string): boolean {
  try {
    return sessionStorage.getItem(dismissStorageKey(versionId)) === '1'
  } catch {
    return false
  }
}

export function markDismissed(versionId: string): void {
  try {
    sessionStorage.setItem(dismissStorageKey(versionId), '1')
  } catch {
    // sessionStorage may be unavailable (private window, quota) — banner just stays visible
  }
}

// Cleared after a successful alignment so the banner can re-arm itself if the BE later
// flags a new mismatch (e.g. user adds lines with a stale VAT).
export function clearDismissed(versionId: string): void {
  try {
    sessionStorage.removeItem(dismissStorageKey(versionId))
  } catch {
    // ignore — best-effort cleanup
  }
}
